import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ModalService } from '@inst-iot/bosch-angular-ui-components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NavigationService } from 'src/app/_services/navigation.service';
import { UserService } from 'src/app/_services/user.service';
import { CompanyService } from 'src/app/_services/company.service';
import { VehicleInfoService } from 'src/app/_services/vehicle-info.service';
import { FleetStateService } from 'src/app/_services/fleet-state.service';
import { FleetService } from 'src/app/_services/fleet.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
  title = 'Battery in the Cloud Insights';
  logoUrl: string = environment.appEndpoint + 'assets/img/bosch_icon.svg';
  fleetId: string[] = [];
  companyData: any;
  private readonly unsubscribe$ = new Subject<void>();
  showHeaderMenu: boolean = false;
  currentPage: string = '';
  fleetName: string = '';
  isAdmin: boolean = false;
  private adminStatusPromise: Promise<boolean> | null = null;
  @ViewChild('configChoiceDialog') configChoiceDialog?: TemplateRef<any>;
  readonly configChoiceButtonLabels = {
    manualLabel: 'Manual',
    automaticLabel: 'Automatic'
  };
  // The dialog lib invokes actions via .call(event): arrow wrappers keep the component context.
  readonly manualChoiceAction = (): void => this.onManualChoice();
  readonly automaticChoiceAction = (): void => this.onAutomaticChoice();

  constructor(
    private readonly modalService: ModalService,
    private readonly navigationService: NavigationService,
    private readonly fleetStateService: FleetStateService,
    private readonly userService: UserService,
    private readonly companyService: CompanyService,
    private readonly vehicleInfoService: VehicleInfoService,
    private readonly fleetService: FleetService
  ) {}

  ngOnInit()  {
    const credentials = sessionStorage.getItem(`credentials_${environment.appId}`);
    if(credentials) {
      const parsedCredentials = JSON.parse(credentials);
      this.showHeaderMenu = true;
      this.checkAdminStatus();
      this.companyService.getCompanyData(parsedCredentials.database).pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (company) => {
          this.companyData = company;
          if (this.companyData.companyName) {
            this.checkDatabaseConfiguration(credentials);
          } else {
            this.goTo('customer-data');
            this.showHeaderMenu = false;
          }
        },
        error: (error) => {
          console.error('Error fetching company:', error);
        }
      })
    } else {
      this.goTo('access-denied');
      this.showHeaderMenu = false;
    }

    this.navigationService.currentView$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(page => {
        this.currentPage = page.view;
      })

      this.fleetStateService.fleetId$.pipe(takeUntil(this.unsubscribe$)).subscribe(fleetId => {
        this.fleetId = fleetId;
      });
  }

  goTo(page: string) {
    if (page === 'settings' && !this.canAccessSettings) {
      return;
    }
    this.navigationService.navigateTo(page)
  }

  get canAccessSettings(): boolean {
    return environment.production && this.isAdmin;
  }

  isActive(view: string): boolean {
    return this.currentPage === view;
  }

  checkAdminStatus(): Promise<boolean> {
    if (!this.adminStatusPromise) {
      const adminSecurityGroupIds = ['GroupEverythingSecurityId', 'GroupAdminId'];
      this.adminStatusPromise = new Promise<boolean>((resolve) => {
        this.userService.getSecurityGroups()
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (response: any) => {
              const groups = Array.isArray(response) ? response : (response?.securityGroups || []);
              this.isAdmin = groups.some((group: any) =>
                adminSecurityGroupIds.includes(typeof group === 'string' ? group : group?.id)
              );
              resolve(this.isAdmin);
            },
            error: (error) => {
              console.error('Error fetching security groups:', error);
              this.isAdmin = false;
              resolve(false);
            }
          });
      });
    }
    return this.adminStatusPromise;
  }

  // Re-entry point after the customer data form is saved: resume from the "is the database already configured?" gate.
  onCompanyDataSaved(): void {
    const credentials = sessionStorage.getItem(`credentials_${environment.appId}`);
    if (credentials) {
      this.showHeaderMenu = true;
      this.goTo(''); // clear the stale form view, the gate decides where to go
      this.checkDatabaseConfiguration(credentials);
    }
  }

  checkDatabaseConfiguration(credentials: any): void {
    if (!environment.production) {
      this.retrieveFleetIdByDbName(credentials);
      return;
    }

    this.fleetService.isDatabaseConfigured().pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        // 200: database already configured -> proceed with the normal flow
        this.retrieveFleetIdByDbName(credentials);
      },
      error: (error) => {
        // 401 (tech user missing) or 403 (group unassigned) -> "Is an administrator user?" branch.
        this.checkAdminStatus().then((isAdmin) => {
          if (!isAdmin) {
            // Non-admin: land on the persistent informational page (the 'User strolled in the view' node of the flow).
            this.showHeaderMenu = false;
            this.goTo('configuration-pending');
            return;
          }
          // Admin: subscribe the fleet (prod-only POST /vehicles), then let the user pick Manual / Automatic.
          if (environment.production) {
            this.vehicleInfoService.storeVehicles({}).pipe(takeUntil(this.unsubscribe$)).subscribe({
              next: () => this.openChooseConfigurationDialog(),
              error: (err) => {
                console.error('Error subscribing to service:', err);
              }
            });
          } else {
            this.openChooseConfigurationDialog();
          }
        });
      }
    });
  }

  retrieveFleetIdByDbName(credentials: any) {
    try {
      const parsedCredentials = JSON.parse(credentials);
      if (parsedCredentials.database) {
        this.fleetName = parsedCredentials.database;
        this.userService.getFleetIdByDb(parsedCredentials.database).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: (fleet) => {
            this.fleetId = fleet._id ? [fleet._id] : [];
            this.vehicleInfoService.getVehiclesWithPagination(1, 0, this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe({
              next: (vehicles) => {
                if (vehicles.data.length > 0) {
                  this.goTo('fleet')
                } else {
                  this.goTo('onboarding')
                }
              },
              error: (error) => {
                console.error('Error fetching vehicles:', error);
                this.goTo('fleet')
              }
            })
          },
          error: (error) => {
            console.error('Error fetching fleet Id:', error);
          },
        });
      } else {
        console.error('Failed to parse credentials from localStorage:', 'Database name is missing in credentials');
      }
    } catch (error) {
      console.error('Failed to parse credentials from localStorage:', error);
    }
  }

  openDialog(tpl: TemplateRef<any>, onClose?: (closeReason: any) => void) {
    this.modalService.open(tpl, { maxWidth: '650px' }).then((close) => {
      if (onClose) {
        onClose(close);
      }
    });
  }

  openChooseConfigurationDialog(): void {
    if (this.configChoiceDialog) {
      // Manual / Automatic are handled by the in-template button bindings; closing the dialog
      // with X/ESC (closeReason undefined) falls back to the manual-configuration page, since
      // manual setup is the only path that needs no action from our side.
      this.openDialog(this.configChoiceDialog, (close) => {
        if (close === undefined) {
          this.showHeaderMenu = false;
          this.goTo('manual-configuration');
        }
      });
    }
  }

  onManualChoice(): void {
    // Manual: the admin configures everything by hand in MyGeotab; land on the persistent instructions page.
    this.showHeaderMenu = false;
    this.goTo('manual-configuration');
  }

  onAutomaticChoice(): void {
    // Automatic: creates the "bitc" group + tech user and links them (prod-only); vehicles get the group later via Assets or Settings.
    const loadFleetAndOpenSettings = (): void => {
      const credentials = sessionStorage.getItem(`credentials_${environment.appId}`);
      if (!credentials) {
        console.error('Cannot open settings: credentials are missing.');
        return;
      }

      try {
        const database = JSON.parse(credentials).database;
        if (!database) {
          console.error('Cannot open settings: database name is missing in credentials.');
          return;
        }

        this.userService.getFleetIdByDb(database).pipe(takeUntil(this.unsubscribe$)).subscribe({
          next: (fleet) => {
            if (!fleet?._id) {
              console.error('Cannot open settings: fleet ID was not returned.');
              return;
            }
            this.fleetName = database;
            this.fleetId = [fleet._id];
            this.goTo('settings');
          },
          error: (error) => {
            console.error('Error fetching fleet Id:', error);
          }
        });
      } catch (error) {
        console.error('Cannot open settings: failed to parse credentials.', error);
      }
    };

    if (environment.production) {
      this.userService.createGroupAndTechUser().pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: () => loadFleetAndOpenSettings(),
        error: (err) => {
          console.error('Error creating group and technical user:', err);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
